import { useEffect } from "react";
import { Volume2 } from "lucide-react";

function supportsSpeechSynthesis() {
  return (
    typeof window !== "undefined" &&
    "speechSynthesis" in window &&
    "SpeechSynthesisUtterance" in window
  );
}

function speakEnglish(text) {
  if (!supportsSpeechSynthesis()) return;

  const synthesis = window.speechSynthesis;
  const utterance = new window.SpeechSynthesisUtterance(text);
  const voices = synthesis.getVoices();

  utterance.lang = "en-US";
  utterance.rate = 0.85;
  utterance.voice =
    voices.find((voice) => voice.lang.toLowerCase() === "en-us") ??
    voices.find((voice) => voice.lang.toLowerCase().startsWith("en")) ??
    null;

  synthesis.cancel();
  synthesis.speak(utterance);
}

function ContextText({ text, word }) {
  const wordIndex = text.toLowerCase().indexOf(word.toLowerCase());
  if (wordIndex === -1) return text;

  return (
    <>
      {text.slice(0, wordIndex)}
      <strong>{text.slice(wordIndex, wordIndex + word.length)}</strong>
      {text.slice(wordIndex + word.length)}
    </>
  );
}

function QuizSentence({ label, text, word, speechLabel, speechSupported }) {
  return (
    <div className="quiz-sentence">
      <p className="quiz-sentence-label">{label}</p>
      <div className="quiz-context">
        <blockquote>
          <ContextText text={text} word={word} />
        </blockquote>
        <button
          type="button"
          className="context-pronunciation-button"
          aria-label={speechLabel}
          title={
            speechSupported ? speechLabel : "当前浏览器不支持语音播放"
          }
          disabled={!speechSupported}
          onClick={() => speakEnglish(text)}
        >
          <Volume2 aria-hidden="true" size={17} strokeWidth={2} />
        </button>
      </div>
    </div>
  );
}

function WordQuizDrawer({ quiz, onClose, onRetry, onChooseMeaning }) {
  const speechSupported = supportsSpeechSynthesis();

  useEffect(
    () => () => {
      if (supportsSpeechSynthesis()) {
        window.speechSynthesis.cancel();
      }
    },
    [],
  );

  return (
    <>
      <button
        type="button"
        className="quiz-backdrop"
        aria-label="关闭词义练习"
        onClick={onClose}
      />
      <aside
        className="word-quiz-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="word-quiz-title"
      >
        <header className="quiz-header">
          <div>
            <p>CONTEXT CLUE</p>
            <div className="quiz-title-row">
              <h2 id="word-quiz-title">{quiz.word}</h2>
              <button
                type="button"
                className="pronunciation-button"
                aria-label={`播放 ${quiz.word} 的发音`}
                title={
                  speechSupported
                    ? "播放美式发音"
                    : "当前浏览器不支持语音播放"
                }
                disabled={!speechSupported}
                onClick={() => speakEnglish(quiz.word)}
              >
                <Volume2 aria-hidden="true" size={18} strokeWidth={2} />
              </button>
            </div>
          </div>
          <button
            type="button"
            className="quiz-close"
            aria-label="关闭词义练习"
            onClick={onClose}
          >
            ×
          </button>
        </header>

        <div className="quiz-body">
          <div className="quiz-sentences">
            <QuizSentence
              label="原文语境"
              text={quiz.context}
              word={quiz.word}
              speechLabel="播放上下文句子"
              speechSupported={speechSupported}
            />
            {quiz.status === "ready" && quiz.data?.sentence && (
              <QuizSentence
                label="辅助例句"
                text={quiz.data.sentence}
                word={quiz.word}
                speechLabel="播放辅助例句"
                speechSupported={speechSupported}
              />
            )}
          </div>

          {quiz.status === "loading" && (
            <div className="quiz-loading" role="status">
              <span>正在准备上下文练习...</span>
              <i />
              <i />
              <i />
            </div>
          )}

          {quiz.status === "error" && (
            <div className="quiz-error" role="alert">
              <p>{quiz.error}</p>
              <button type="button" onClick={onRetry}>
                重新加载
              </button>
            </div>
          )}

          {quiz.status === "ready" && quiz.data && (
            <section className="quiz-content">
              <p className="quiz-prompt">
                根据上下文，你觉得这里更接近哪个意思？
              </p>
              <div className="quiz-options">
                {quiz.data.options.map((option, index) => {
                  const answered = quiz.selectedIndex !== null;
                  const isSelected = quiz.selectedIndex === index;
                  const optionClass = [
                    "quiz-option",
                    answered && option.isCorrect ? "correct" : "",
                    answered && isSelected && !option.isCorrect
                      ? "incorrect"
                      : "",
                  ]
                    .filter(Boolean)
                    .join(" ");

                  return (
                    <button
                      type="button"
                      className={optionClass}
                      disabled={answered}
                      key={`${option.meaning}-${index}`}
                      onClick={() => onChooseMeaning(index)}
                    >
                      <span>{String.fromCharCode(65 + index)}</span>
                      <strong>{option.meaning}</strong>
                    </button>
                  );
                })}
              </div>

              {quiz.selectedIndex !== null && (
                <div className="quiz-reveal" aria-live="polite">
                  <p
                    className={
                      quiz.data.options[quiz.selectedIndex].isCorrect
                        ? "quiz-result correct"
                        : "quiz-result incorrect"
                    }
                  >
                    {quiz.data.options[quiz.selectedIndex].isCorrect
                      ? "判断正确"
                      : "再看一次上下文：正确答案已标出"}
                  </p>
                  <section>
                    <h3>完整释义</h3>
                    <p>{quiz.data.meaning}</p>
                  </section>
                  <section>
                    <h3>记忆提示</h3>
                    <p>{quiz.data.tip}</p>
                  </section>
                </div>
              )}
            </section>
          )}
        </div>
      </aside>
    </>
  );
}

export default WordQuizDrawer;
